import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { useAuth } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public details?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 401:
        return 'Please log in to continue.';
      case 403:
        return "You don't have permission to do this.";
      case 404:
        return "The resource you're looking for doesn't exist.";
      case 422:
        if (error.details) {
          return Object.values(error.details).flat().join('. ');
        }
        return 'Please check your input.';
      case 429:
        return 'Too many requests. Please wait a moment.';
      default:
        return 'Something went wrong. Please try again.';
    }
  }

  if (error instanceof AxiosError) {
    if (error.message === 'Network Error') {
      return 'Cannot connect to server. Check your internet connection.';
    }
    if (error.response?.data?.error?.message) {
      return error.response.data.error.message;
    }
  }

  return 'An unexpected error occurred.';
}

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const token = localStorage.getItem('accessToken');

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...config.headers as Record<string, string>,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await axios({
      baseURL: API_BASE_URL,
      ...config,
      headers,
      withCredentials: true,
    });

    return response.data.data ?? response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      const { error: errorData } = error.response.data;

      if (error.response.status === 401) {
        // Try to refresh token
        try {
          const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
            withCredentials: true,
          });

          if (refreshResponse.data.data?.accessToken) {
            localStorage.setItem('accessToken', refreshResponse.data.data.accessToken);

            // Retry original request
            const retryResponse = await axios({
              baseURL: API_BASE_URL,
              ...config,
              headers: { ...headers, Authorization: `Bearer ${refreshResponse.data.data.accessToken}` },
              withCredentials: true,
            });

            return retryResponse.data.data ?? retryResponse.data;
          }
        } catch {
          // Refresh failed, redirect to login
          localStorage.removeItem('accessToken');
          window.location.href = '/login';
        }
      }

      throw new ApiError(
        errorData?.message || 'An error occurred',
        errorData?.code || 'UNKNOWN',
        error.response.status,
        errorData?.details
      );
    }

    throw new ApiError(getErrorMessage(error), 'UNKNOWN', 500);
  }
}

export const api = {
  get: <T>(url: string, params?: Record<string, unknown>) =>
    request<T>({ method: 'GET', url, params }),

  post: <T>(url: string, data?: unknown) =>
    request<T>({ method: 'POST', url, data }),

  put: <T>(url: string, data?: unknown) =>
    request<T>({ method: 'PUT', url, data }),

  patch: <T>(url: string, data?: unknown) =>
    request<T>({ method: 'PATCH', url, data }),

  delete: <T>(url: string) =>
    request<T>({ method: 'DELETE', url }),
};

export function handleApiError(error: unknown) {
  const message = getErrorMessage(error);
  toast.error(message);
  return new ApiError(message, 'UNKNOWN', 500);
}
